import AcUnitIcon from '@mui/icons-material/AcUnit';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BugReportIcon from '@mui/icons-material/BugReport';
import BuildIcon from '@mui/icons-material/Build';
import BusinessIcon from '@mui/icons-material/Business';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import EmailIcon from '@mui/icons-material/Email';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import GrainIcon from '@mui/icons-material/Grain';
import GroupIcon from '@mui/icons-material/Group';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HomeIcon from '@mui/icons-material/Home';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import PaymentIcon from '@mui/icons-material/Payment';
import PeopleIcon from '@mui/icons-material/People';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import RestaurantMenuOutlinedIcon from '@mui/icons-material/RestaurantMenuOutlined';
import RoomPreferencesIcon from '@mui/icons-material/RoomPreferences';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import ThermostatOutlinedIcon from '@mui/icons-material/ThermostatOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import type { TFunction } from 'i18next';
import { MODULE_ACCENT_COLORS } from '../theme/module-colors';
import { isBetaRoute } from './beta-routes';
import { RoleNames } from './role-names';
import { RoutesList } from './routes-list';

export { RoutesList };

interface RouteInfo {
  label: string;
  path: string;
  icon: React.ReactNode;
  isLoggedIn: boolean;
  accentColor: string;
  adminOnly?: boolean;
  hideOnMobile?: boolean;
  external?: boolean;
  /** Shows the "Beta" chip next to the entry in the side drawer. */
  beta?: boolean;
  hideForRoles?: string[];
  /**
   * Whitelist of roles allowed to see this item. If set, the item is hidden
   * for any other role (overrides default visibility for Platform Admin too).
   */
  showForRoles?: string[];
  /**
   * Hides the item when the tenant does not have the DDT add-on enabled
   * (`SubscriptionInfo.ddtAddonEnabled`), regardless of permission.
   */
  requiresDdtAddon?: boolean;
  /**
   * Hides the item when the tenant does not have the IoT add-on enabled
   * (`SubscriptionInfo.iotAddonEnabled`), regardless of permission — IoT-only
   * settings (es. Preset alimenti per le sonde) non hanno senso senza sonde.
   */
  requiresIotAddon?: boolean;
  /**
   * Hides the item when the tenant does not have the Survey add-on enabled
   * (`SubscriptionInfo.surveyAddonEnabled`), unless the current user is the
   * Consulente role (who always sees it, since Survey is their tool).
   */
  requiresSurveyAddon?: boolean;
  /**
   * Hides the item when the tenant does not have the prepackaged-label area
   * enabled (`SubscriptionInfo.prepackagedLabelEnabled`), unless the current
   * user is the Consulente role, mirroring the backend
   * PrepackagedLabelAddonGuard.
   */
  requiresPrepackagedLabelAddon?: boolean;
  /**
   * Hides the item when the tenant does not have the Menu add-on enabled
   * (`SubscriptionInfo.menuAddonEnabled`), mirroring the backend
   * MenuAddonGuard.
   */
  requiresMenuAddon?: boolean;
  /**
   * Hides the item unless the user holds at least one membership (any
   * tenant) with the Consulente role. Evaluated via `useConsultantAccess()`
   * (`GET /consultant/access`) — NOT the tenant-scoped `roleName` — because
   * a consultant currently switched into one of their own tenants (e.g. as
   * Owner) must still see their cross-tenant client list. Link referral e
   * assegnazioni manuali (partner/procacciatori) non contano.
   */
  requiresConsultantAccess?: boolean;
  /**
   * Hides the item unless `isPartner` from `useConsultantAccess()`
   * (`GET /consultant/access`) is true — the user is system Consulente OR
   * Owner of a tenant with an active PartnerProfile, not necessarily the
   * current one, so the item survives any company switch.
   *
   * Asimmetria accettata: l'API `/consultant/partner` resta raggiungibile
   * per un Owner-partner puro anche senza consultant access, ma qui la voce
   * resta comunque dentro gli "Strumenti del consulente" e quindi richiede
   * anche `requiresConsultantAccess` (il partner "classico" è Consulente nei
   * workspace dei propri clienti). Always pair it with
   * `requiresConsultantAccess`.
   */
  requiresPartnerAccess?: boolean;
  /**
   * Hides the item unless the user holds Owner/Manager membership on 2+
   * tenants. Evaluated via `useMultiSiteAccess()` (`GET /my-sites/access`) —
   * user-scoped, like `requiresConsultantAccess`, NOT the tenant-scoped
   * `roleName`. Unrelated to consultant access: this item never goes in the
   * `consultantTools` section.
   */
  requiresMultiSiteAccess?: boolean;
  /**
   * Hides the item whenever the user has consultant access, even if the
   * role-based rules above would show it. Used by the twin entry in the
   * "Strumenti del consulente" section so a user with consultant access
   * never sees the same page twice in the drawer.
   */
  hideWithConsultantAccess?: boolean;
}

export interface NavSection {
  title?: string;
  items: RouteInfo[];
  /**
   * Renders the section as the consultant toolbox: tinted background panel,
   * visible only to users with consultant access (every item inside carries
   * `requiresConsultantAccess`).
   */
  consultantTools?: boolean;
}

const ACCENT_COLORS = {
  home: '#6B46C1', // Viola (primary)
  haccp: '#0D9488', // Teal
  temperature: '#9333EA', // Viola
  oil: '#D97706', // Ambra/Arancione
  cleaning: '#0EA5E9', // Azzurro
  pest: '#84CC16', // Verde lime
  blastChilling: '#0891B2', // Ciano scuro (freddo)
  cookingTemperature: '#EF4444', // Rosso caldo (cottura)
  pestPoints: '#65A30D', // Verde lime scuro
  receiving: '#8B5CF6', // Viola chiaro
  suppliers: '#EC4899', // Rosa
  lots: '#14B8A6', // Teal chiaro
  recipes: '#F97316', // Arancione
  ingredients: '#65A30D', // Verde lime scuro (allineato a MODULE_ACCENT_COLORS.ingredients)
  menus: '#E11D48', // Rosa scuro
  production: '#10B981', // Verde smeraldo
  equipment: '#6366F1', // Indaco
  refrigerators: '#3B82F6', // Blu (freddo)
  fryers: '#F97316', // Arancione fuoco (caldo)
  cleaningAreas: '#06B6D4', // Ciano
  documents: '#7C3AED', // Viola
  regulatoryNews: '#0369A1', // Blu istituzionale
  team: '#2563EB', // Blu
  settings: '#78716C', // Grigio neutro
  auth: '#64748B', // Grigio slate
  admin: '#E65100', // Deep orange — distinct admin color
  surveys: '#0F766E', // Teal-700 — surveys module
  customers: '#16A34A', // Verde — clienti
  transportDocuments: '#0891B2', // Ciano — documenti di trasporto
  consultantClients: '#0E7490', // Ciano scuro — clienti del consulente HACCP
  consultantClientStats: '#0E7490', // Ciano scuro — statistiche clienti del consulente
  consultantPartner: '#0E7490', // Ciano scuro — area partner del consulente
  consultantSurveyStudio: '#0E7490', // Ciano scuro — Survey Studio del consulente
  consultantRequests: '#0E7490', // Ciano scuro — richieste survey ai clienti del consulente
  consultantStudioTemplates: '#0E7490', // Ciano scuro — template studio del consulente
  mySites: '#4F46E5', // Indaco — portfolio "Le mie sedi" (Owner/Manager multi-sede)
};

export function getNavSections(t: TFunction): NavSection[] {
  return buildNavSections(t).map((section) => ({
    ...section,
    items: section.items.map((item) => ({ ...item, beta: isBetaRoute(item.path) })),
  }));
}

function buildNavSections(t: TFunction): NavSection[] {
  return [
    {
      items: [
        {
          label: t('nav.home'),
          path: RoutesList.HOME,
          icon: <HomeIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.home,
        },
        {
          label: t('nav.haccpDashboard'),
          path: RoutesList.HACCP,
          icon: <AssessmentIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.haccp,
          hideOnMobile: true,
        },
        {
          label: t('nav.mySites'),
          path: RoutesList.MY_SITES,
          icon: <BusinessOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.mySites,
          requiresMultiSiteAccess: true,
        },
      ],
    },
    {
      title: t('nav.consultantTools'),
      consultantTools: true,
      items: [
        {
          label: t('surveys.menu'),
          path: RoutesList.SURVEYS,
          icon: <AssignmentIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.surveys,
          requiresConsultantAccess: true,
        },
        {
          label: t('consultant.surveyStudio.menuTitle'),
          path: RoutesList.CONSULTANT_SURVEYS,
          icon: <FactCheckOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.consultantSurveyStudio,
          requiresConsultantAccess: true,
        },
        {
          label: t('consultant.clients.menuTitle'),
          path: RoutesList.CONSULTANT_CLIENTS,
          icon: <SupervisorAccountOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.consultantClients,
          requiresConsultantAccess: true,
        },
        {
          label: t('consultant.deadlines.menuTitle'),
          path: RoutesList.CONSULTANT_DEADLINES,
          icon: <EventNoteOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.documents,
          requiresConsultantAccess: true,
        },
        {
          label: t('surveyInvite.requests.menuTitle'),
          path: RoutesList.CONSULTANT_REQUESTS,
          icon: <ForwardToInboxOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.consultantRequests,
          requiresConsultantAccess: true,
        },
        {
          label: t('studioTemplates.menuTitle'),
          path: RoutesList.CONSULTANT_STUDIO_TEMPLATES,
          icon: <ContentCopyOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.consultantStudioTemplates,
          requiresConsultantAccess: true,
        },
        {
          label: t('consultant.clientStats.menuTitle'),
          path: RoutesList.CONSULTANT_CLIENT_STATS,
          icon: <InsightsOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.consultantClientStats,
          requiresConsultantAccess: true,
        },
        {
          label: t('consultant.partner.menuTitle'),
          path: RoutesList.CONSULTANT_PARTNER,
          icon: <HandshakeIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.consultantPartner,
          requiresConsultantAccess: true,
          requiresPartnerAccess: true,
        },
      ],
    },
    {
      title: t('nav.autocontrollo'),
      items: [
        {
          label: t('tenantStats.menuTitle'),
          path: RoutesList.TENANT_STATS,
          icon: <InsightsOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.haccp,
        },
        {
          label: t('nav.temperature'),
          path: RoutesList.SELF_CHECK_TEMPERATURE,
          icon: <ThermostatIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.temperature,
        },
        {
          label: t('nav.oilChange'),
          path: RoutesList.SELF_CHECK_OIL_CHANGE,
          icon: <OilBarrelIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.oil,
        },
        {
          label: t('nav.pestControl'),
          path: RoutesList.SELF_CHECK_PEST_CONTROL,
          icon: <BugReportIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.pest,
        },
        {
          label: t('nav.metalDetection'),
          path: RoutesList.SELF_CHECK_METAL_DETECTION,
          icon: <ScienceOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.pest,
        },
        {
          label: t('nav.cleaning'),
          path: RoutesList.SELF_CHECK_CLEANING,
          icon: <CleaningServicesIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.cleaning,
        },
      ],
    },
    {
      title: t('nav.produzione'),
      items: [
        {
          label: t('nav.cookingTemperature'),
          path: RoutesList.SELF_CHECK_COOKING_TEMPERATURE,
          icon: <LocalFireDepartmentIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.cookingTemperature,
        },
        {
          label: t('nav.blastChilling'),
          path: RoutesList.SELF_CHECK_BLAST_CHILLING,
          icon: <AcUnitIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.blastChilling,
        },
      ],
    },
    {
      title: t('nav.magazzino'),
      items: [
        {
          label: t('nav.goodsReceiving'),
          path: RoutesList.GOODS_RECEIVING,
          icon: <LocalShippingIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.receiving,
        },
        {
          label: t('nav.lots'),
          path: RoutesList.LOTS,
          icon: <QrCodeIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.lots,
        },
        {
          label: t('nav.recalls'),
          path: RoutesList.RECALLS,
          icon: <ReportProblemOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.lots,
        },
      ],
    },
    {
      title: t('nav.ricettario'),
      items: [
        {
          label: t('nav.ingredients'),
          path: RoutesList.INGREDIENTS,
          icon: <GrainIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.ingredients,
        },
        {
          label: t('nav.recipes'),
          path: RoutesList.RECIPES,
          icon: <MenuBookIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.recipes,
        },
        {
          label: t('nav.labels'),
          path: RoutesList.LABELS,
          icon: <PrintOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.recipes,
          requiresPrepackagedLabelAddon: true,
        },
        {
          label: t('nav.menus', { defaultValue: 'Menu' }),
          path: RoutesList.MENUS,
          icon: <RestaurantMenuOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.menus,
          requiresMenuAddon: true,
        },
      ],
    },
    {
      title: t('nav.gestione'),
      items: [
        {
          label: t('nav.suppliers'),
          path: RoutesList.SUPPLIERS,
          icon: <BusinessIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.suppliers,
        },
        {
          label: t('nav.customers'),
          path: RoutesList.CUSTOMERS,
          icon: <PeopleOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.customers,
          requiresDdtAddon: true,
        },
        {
          label: t('nav.transportDocuments'),
          path: RoutesList.TRANSPORT_DOCUMENTS,
          icon: <ReceiptLongOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.transportDocuments,
          requiresDdtAddon: true,
        },
        {
          label: t('nav.equipment'),
          path: RoutesList.EQUIPMENT,
          icon: <BuildIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.equipment,
        },
        {
          label: t('nav.ambientRegistry'),
          path: RoutesList.AMBIENT_REGISTRY,
          icon: <DeviceThermostatIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.equipment,
          // Il registro esiste solo con le sonde: senza add-on IoT non c'è
          // nulla da registrare (stessa regola dei preset sonde).
          requiresIotAddon: true,
        },
        {
          label: t('nav.cleaningAreas'),
          path: RoutesList.CLEANING_AREAS,
          icon: <RoomPreferencesIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.cleaningAreas,
        },
        {
          label: t('nav.documents'),
          path: RoutesList.DOCUMENTS,
          icon: <DescriptionIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.documents,
        },
        {
          label: t('nav.regulatoryNews'),
          path: RoutesList.REGULATORY_NEWS,
          icon: <NewspaperIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.regulatoryNews,
        },
        {
          label: t('surveys.menu'),
          path: RoutesList.SURVEYS,
          icon: <AssignmentIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.surveys,
          showForRoles: [RoleNames.CONSULTANT, RoleNames.OWNER, RoleNames.MANAGER],
          requiresSurveyAddon: true,
          // Consulenti (e chi ha accesso consulente) lo trovano nella sezione
          // "Strumenti del consulente": qui resta solo per Owner/Manager.
          hideWithConsultantAccess: true,
        },
      ],
    },
    {
      title: t('admin.section'),
      items: [
        {
          label: t('admin.dashboard.title'),
          path: RoutesList.ADMIN_DASHBOARD,
          icon: <DashboardIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.tenants.title'),
          path: RoutesList.ADMIN_TENANTS,
          icon: <BusinessIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.users.title'),
          path: RoutesList.ADMIN_USERS,
          icon: <PeopleIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },

        {
          label: t('admin.documentTypes.title'),
          path: RoutesList.ADMIN_DOCUMENT_TYPES,
          icon: <DescriptionIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.discountCodes.pageTitle'),
          path: RoutesList.ADMIN_DISCOUNT_CODES,
          icon: <LocalOfferIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.invites.title'),
          path: RoutesList.ADMIN_INVITES,
          icon: <PersonAddIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.signupLinks.title'),
          path: RoutesList.ADMIN_SIGNUP_LINKS,
          icon: <LinkIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.regulatoryNews.title'),
          path: RoutesList.ADMIN_REGULATORY_NEWS,
          icon: <NewspaperIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.trialConfig.title'),
          path: RoutesList.ADMIN_TRIAL_CONFIG,
          icon: <HourglassEmptyIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.emailTemplates.title'),
          path: RoutesList.ADMIN_EMAIL_TEMPLATES,
          icon: <EmailIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: 'QR Generator',
          path: RoutesList.ADMIN_QR_GENERATOR,
          icon: <QrCodeIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.surveys.list.title'),
          path: RoutesList.ADMIN_SURVEYS,
          icon: <AssignmentIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.labelRulePacks.title'),
          path: RoutesList.ADMIN_LABEL_RULE_PACKS,
          icon: <FactCheckOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.eInvoices.title'),
          path: RoutesList.ADMIN_E_INVOICES,
          icon: <ReceiptLongIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.whatsapp.menuTitle'),
          path: RoutesList.ADMIN_WHATSAPP,
          icon: <ForumOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.iot.provisioning.title'),
          path: RoutesList.ADMIN_IOT_PROVISIONING,
          icon: <SettingsEthernetIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.iotConfig.title'),
          path: RoutesList.ADMIN_IOT_CONFIG,
          icon: <TuneIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.ingredients.title'),
          path: RoutesList.ADMIN_INGREDIENTS,
          icon: <ScienceOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.partners.title'),
          path: RoutesList.ADMIN_PARTNERS,
          icon: <BusinessOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
        {
          label: t('admin.insights.title'),
          path: RoutesList.ADMIN_INSIGHTS,
          icon: <MonitorHeartOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.admin,
          adminOnly: true,
        },
      ],
    },
    {
      title: t('nav.account'),
      items: [
        {
          label: t('notifications.page.title'),
          path: RoutesList.NOTIFICATIONS,
          icon: <NotificationsOutlinedIcon />,
          isLoggedIn: true,
          accentColor: MODULE_ACCENT_COLORS.notifications.main,
        },
        {
          label: t('nav.team'),
          path: RoutesList.TEAM,
          icon: <GroupIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.team,
        },
        {
          label: t('nav.profile'),
          path: RoutesList.PROFILE,
          icon: <PersonIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.settings,
        },
        {
          label: t('nav.settings'),
          path: RoutesList.SETTINGS,
          icon: <SettingsIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.settings,
        },
        {
          label: t('nav.billing'),
          path: RoutesList.BILLING,
          icon: <PaymentIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.settings,
        },
        {
          label: t('nav.closingDays'),
          path: RoutesList.CLOSING_DAYS,
          icon: <EventBusyIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.settings,
        },
        {
          label: t('nav.emailPreferences'),
          path: RoutesList.EMAIL_PREFERENCES,
          icon: <EmailIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.settings,
        },
        {
          label: t('nav.labelFormat'),
          path: RoutesList.LABEL_FORMAT,
          icon: <PrintOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.settings,
        },
        {
          label: t('nav.companyProfile'),
          path: RoutesList.COMPANY_PROFILE,
          icon: <BusinessOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.settings,
        },
        {
          label: t('nav.foodPresets'),
          path: RoutesList.FOOD_PRESETS,
          icon: <ThermostatOutlinedIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.settings,
          requiresIotAddon: true,
        },
      ],
    },
    {
      title: t('nav.help'),
      items: [
        {
          label: t('nav.guide'),
          path: RoutesList.GUIDE,
          icon: <MenuBookIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.home,
          external: true,
        },
      ],
    },
    {
      items: [
        {
          label: t('nav.login'),
          path: RoutesList.LOGIN,
          icon: <LoginIcon />,
          isLoggedIn: false,
          accentColor: ACCENT_COLORS.auth,
        },
        {
          label: t('nav.register'),
          path: RoutesList.REGISTER,
          icon: <PersonAddIcon />,
          isLoggedIn: false,
          accentColor: ACCENT_COLORS.auth,
        },
        {
          label: t('nav.logout'),
          path: RoutesList.LOGOUT,
          icon: <LogoutIcon />,
          isLoggedIn: true,
          accentColor: ACCENT_COLORS.auth,
        },
      ],
    },
  ];
}

/**
 * Trova le informazioni della rotta corrente dato il pathname.
 * Gestisce rotte nested (es. /temperature-check/record/step/0 -> "Temperature")
 */
export function getRouteInfo(pathname: string, navSections: NavSection[]): RouteInfo | undefined {
  const allRoutes = navSections.flatMap((section) => section.items);

  // Match esatto per "/" (Home)
  if (pathname === '/') {
    return allRoutes.find((r) => r.path === '/');
  }

  // Per altre rotte, scegli il match più specifico (path più lungo) per gestire rotte nested
  return allRoutes
    .filter((r) => r.path !== '/' && pathname.startsWith(r.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
}
